import React, { useState } from 'react';
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { cn } from "../../utils/cn";
import Icon from '../AppIcon';

const buttonVariants = cva(
    "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 active:scale-95 relative overflow-hidden",
    {
        variants: {
            variant: {
                default: "bg-primary text-primary-foreground hover:bg-primary/90 hover:shadow-lg",
                destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90 hover:shadow-lg",
                outline: "border border-input hover:bg-accent hover:text-accent-foreground hover:shadow-md",
                secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80 hover:shadow-md",
                ghost: "hover:bg-accent hover:text-accent-foreground",
                link: "text-primary underline-offset-4 hover:underline",
                success: "bg-success text-success-foreground hover:bg-success/90 hover:shadow-lg",
                warning: "bg-warning text-warning-foreground hover:bg-warning/90 hover:shadow-lg",
                danger: "bg-error text-error-foreground hover:bg-error/90 hover:shadow-lg",
            },
            size: {
                default: "h-10 px-4 py-2",
                sm: "h-9 rounded-md px-3",
                lg: "h-11 rounded-md px-8",
                icon: "h-10 w-10",
                xs: "h-8 rounded-md px-2 text-xs",
                xl: "h-12 rounded-md px-10 text-base",
            },
        },
        defaultVariants: {
            variant: "default",
            size: "default",
        },
    }
);

const Button = React.forwardRef(({
    className,
    variant,
    size,
    asChild = false,
    children,
    loading = false,
    iconName = null,
    iconPosition = 'left',
    iconSize = null,
    fullWidth = false,
    disabled = false,
    disabledTooltip,
    onClick,
    ...props
}, ref) => {
    const Comp = asChild ? Slot : "button";
    const [ripples, setRipples] = useState([]);
    const [showDisabledTooltip, setShowDisabledTooltip] = useState(false);

    // Icon size mapping based on button size
    const iconSizeMap = {
        xs: 12,
        sm: 14,
        default: 16,
        lg: 18,
        xl: 20,
        icon: 16,
    };

    const calculatedIconSize = iconSize || iconSizeMap?.[size] || 16;

    // Ripple effect handler
    const handleClick = (e) => {
        const button = e?.currentTarget;
        const rect = button?.getBoundingClientRect();
        const size = Math.max(rect?.width, rect?.height);
        const x = e?.clientX - rect?.left - size / 2;
        const y = e?.clientY - rect?.top - size / 2;
        
        const newRipple = {
            x,
            y,
            size,
            id: Date.now()
        };

        setRipples(prev => [...prev, newRipple]);

        // Remove ripple after animation
        setTimeout(() => {
            setRipples(prev => prev?.filter(ripple => ripple?.id !== newRipple?.id));
        }, 600);

        // Call original onClick
        if (onClick) {
            onClick(e);
        }
    };

    // Loading spinner
    const LoadingSpinner = () => (
        <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" aria-hidden="true">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
    );

    const renderIcon = () => {
        if (!iconName) return null;
        try {
            return (
                <Icon
                    name={iconName}
                    size={calculatedIconSize}
                    className={cn(
                        children && iconPosition === 'left' && "mr-2",
                        children && iconPosition === 'right' && "ml-2"
                    )}
                    aria-hidden="true"
                />
            );
        } catch {
            return null;
        }
    };

    const renderFallbackButton = () => (
        <button
            className={cn(
                buttonVariants({ variant, size, className }),
                fullWidth && "w-full"
            )}
            ref={ref}
            disabled={disabled || loading}
            onClick={handleClick}
            {...props}
        >
            {/* Ripple effect */}
            {ripples?.map(ripple => (
                <span
                    key={ripple?.id}
                    className="absolute rounded-full bg-white/30 animate-ripple pointer-events-none"
                    style={{
                        left: ripple?.x,
                        top: ripple?.y,
                        width: ripple?.size,
                        height: ripple?.size,
                    }}
                />
            ))}
            {loading && <LoadingSpinner />}
            {iconName && iconPosition === 'left' && renderIcon()}
            {children}
            {iconName && iconPosition === 'right' && renderIcon()}
        </button>
    );

    const isDisabled = disabled || loading;

    const buttonElement = (
        <button
            disabled={isDisabled}
            className={cn(
                buttonVariants({ variant, size, className }),
                fullWidth && "w-full"
            )}
            ref={ref}
            onClick={handleClick}
            onMouseEnter={() => isDisabled && disabledTooltip && setShowDisabledTooltip(true)}
            onMouseLeave={() => setShowDisabledTooltip(false)}
            {...props}
        >
            {/* Ripple effect */}
            {ripples?.map(ripple => (
                <span
                    key={ripple?.id}
                    className="absolute rounded-full bg-white/30 animate-ripple pointer-events-none"
                    style={{
                        left: ripple?.x,
                        top: ripple?.y,
                        width: ripple?.size,
                        height: ripple?.size,
                    }}
                />
            ))}
            {loading && <LoadingSpinner />}
            {iconName && iconPosition === 'left' && renderIcon()}
            {children}
            {iconName && iconPosition === 'right' && renderIcon()}
        </button>
    );

    if (disabledTooltip && isDisabled) {
        return (
            <div className="relative inline-flex" style={fullWidth ? { width: '100%' } : {}}>
                {buttonElement}
                {showDisabledTooltip && (
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 w-56 px-3 py-2 bg-foreground text-background text-xs rounded-lg shadow-lg pointer-events-none text-center">
                        {disabledTooltip}
                        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-foreground" />
                    </div>
                )}
            </div>
        );
    }

    // When asChild is true, merge icons into the child element
    if (asChild) {
        try {
            if (!children || React.Children?.count(children) !== 1) {
                return renderFallbackButton();
            }

            const child = React.Children?.only(children);

            if (!React.isValidElement(child)) {
                return renderFallbackButton();
            }
            const content = (
                <>
                    {ripples?.map(ripple => (
                        <span
                            key={ripple?.id}
                            className="absolute rounded-full bg-white/30 animate-ripple pointer-events-none"
                            style={{
                                left: ripple?.x,
                                top: ripple?.y,
                                width: ripple?.size,
                                height: ripple?.size,
                            }}
                        />
                    ))}
                    {loading && <LoadingSpinner />}
                    {iconName && iconPosition === 'left' && renderIcon()}
                    {child?.props?.children}
                    {iconName && iconPosition === 'right' && renderIcon()}
                </>
            );

            const clonedChild = React.cloneElement(child, {
                className: cn(
                    buttonVariants({ variant, size, className }),
                    fullWidth && "w-full",
                    child?.props?.className
                ),
                disabled: disabled || loading || child?.props?.disabled,
                onClick: handleClick,
                children: content,
            });

            return <Comp ref={ref} {...props}>{clonedChild}</Comp>;
        } catch {
            return renderFallbackButton();
        }
    }

    return buttonElement;
});

Button.displayName = "Button";
export default Button;